import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

/**
 * Custom hook for compliance documents data using optimized RPC
 * Fetches personnel certificates (user_qualifications) and site compliance docs (site_compliance_requirements)
 *
 * @param {string} projectId - The project ID
 * @param {object} options - Configuration options
 * @param {string[]} options.projectIds - Project IDs for stack view
 * @param {boolean} options.isStackView - Whether this is a stack view
 * @param {string} options.selectedSpaceFilter - Selected space filter for stack view
 * @param {boolean} options.enabled - Whether to fetch data
 * @param {number} options.personnelLimit - Limit for personnel certs pagination
 * @param {number} options.personnelOffset - Offset for personnel certs pagination
 * @param {number} options.siteLimit - Limit for site docs pagination
 * @param {number} options.siteOffset - Offset for site docs pagination
 * @param {string} options.statusFilter - Filter by compliance status
 * @param {string} options.categoryFilter - Filter by category
 * @param {string} options.searchQuery - Search query for filtering
 * @returns {object} Compliance documents data and methods
 */
export const useComplianceDocs = (projectId, options = {}) => {
  const {
    projectIds = null,
    isStackView = false,
    isGlobalView = false,
    selectedSpaceFilter = 'all',
    enabled = true,
    personnelLimit = 100,
    personnelOffset = 0,
    siteLimit = 100,
    siteOffset = 0,
    statusFilter = null,
    categoryFilter = null,
    searchQuery = null
  } = options;

  // Data state
  const [personnelCerts, setPersonnelCerts] = useState([]);
  const [siteDocs, setSiteDocs] = useState([]);
  const [stats, setStats] = useState({
    personnel: { total: 0, valid: 0, expiring: 0, expired: 0, missing: 0 },
    site: { total: 0, compliant: 0, dueSoon: 0, overdue: 0, notStarted: 0 },
    policies: { total: 0, pending: 0, overdue: 0, acknowledged: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get effective project IDs based on filters
  const getEffectiveProjectIds = useCallback(() => {
    if (!isStackView && !isGlobalView) return null; // RPC will use p_project_id
    if (selectedSpaceFilter === 'all') return projectIds || [];
    return [selectedSpaceFilter];
  }, [isStackView, isGlobalView, selectedSpaceFilter, projectIds]);

  // Main fetch function using RPC
  const fetchData = useCallback(async () => {
    if (!enabled) return;
    if (!projectId && !isStackView && !isGlobalView) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const effectiveIds = getEffectiveProjectIds();

      const { data, error: rpcError } = await supabase.rpc('get_compliance_docs', {
        p_project_id: (isStackView || isGlobalView) ? null : projectId,
        p_project_ids: effectiveIds,
        p_is_stack_view: isStackView,
        p_is_global_view: isGlobalView,
        p_personnel_limit: personnelLimit,
        p_personnel_offset: personnelOffset,
        p_site_limit: siteLimit,
        p_site_offset: siteOffset,
        p_status_filter: statusFilter,
        p_category_filter: categoryFilter,
        p_search_query: searchQuery
      });

      if (rpcError) throw rpcError;

      // Transform snake_case to camelCase for consistency
      const transformedPersonnel = (data.personnelCerts || []).map(cert => ({
        id: cert.id,
        userId: cert.user_id,
        requirementId: cert.requirement_id,
        status: cert.status,
        expiryDate: cert.expiry_date,
        evidenceUrl: cert.evidence_url,
        evidenceFiles: cert.evidence_files || [],
        certificateNumber: cert.certificate_number,
        verifiedBy: cert.verified_by,
        verifiedDate: cert.verified_date,
        completedDate: cert.completed_date,
        notes: cert.notes,
        evidenceVersionHistory: cert.evidence_version_history || [],
        requirementName: cert.requirement_name,
        category: cert.category,
        requirementType: cert.requirement_type,
        renewalPeriod: cert.renewal_period,
        legalRequirement: cert.legal_requirement,
        projectId: cert.project_id,
        userName: cert.user_name,
        userFirstName: cert.user_first_name,
        userLastName: cert.user_last_name,
        userAvatar: cert.user_avatar,
        userEmail: cert.user_email,
        complianceStatus: cert.compliance_status
      }));

      const transformedSite = (data.siteDocs || []).map(doc => ({
        id: doc.id,
        projectId: doc.project_id,
        projectName: doc.project_name,
        name: doc.name,
        description: doc.description,
        category: doc.category,
        priority: doc.priority,
        requirementType: doc.requirement_type,
        inspectionFrequency: doc.inspection_frequency,
        lastCompletedDate: doc.last_completed_date,
        nextDueDate: doc.next_due_date,
        evidenceUrl: doc.evidence_url,
        evidenceFileId: doc.evidence_file_id,
        evidenceFileName: doc.evidence_file_name,
        evidenceVersionHistory: doc.evidence_version_history,
        responsiblePersonId: doc.responsible_person_id,
        responsibleRole: doc.responsible_role,
        regulatoryBody: doc.regulatory_body,
        regulationReference: doc.regulation_reference,
        legalRequirement: doc.legal_requirement,
        status: doc.status,
        complianceNotes: doc.compliance_notes,
        notes: doc.notes,
        showOnDashboard: doc.show_on_dashboard,
        responsiblePersonName: doc.responsible_person_name,
        responsiblePersonAvatar: doc.responsible_person_avatar,
        responsiblePersonEmail: doc.responsible_person_email,
        complianceStatus: doc.compliance_status
      }));

      setPersonnelCerts(transformedPersonnel);
      setSiteDocs(transformedSite);
      setStats(data.stats || {
        personnel: { total: 0, valid: 0, expiring: 0, expired: 0, missing: 0 },
        site: { total: 0, compliant: 0, dueSoon: 0, overdue: 0, notStarted: 0 },
        policies: { total: 0, pending: 0, overdue: 0, acknowledged: 0 }
      });
    } catch (err) {
      console.error('Error fetching compliance docs:', err);
      setError(err.message);
      toast.error('Failed to load compliance documents');
    } finally {
      setLoading(false);
    }
  }, [
    enabled,
    projectId,
    isStackView,
    isGlobalView,
    getEffectiveProjectIds,
    personnelLimit,
    personnelOffset,
    siteLimit,
    siteOffset,
    statusFilter,
    categoryFilter,
    searchQuery
  ]);

  // Group personnel certs by status
  const groupedPersonnelCerts = useMemo(() => {
    return {
      expired: personnelCerts.filter(c => c.complianceStatus === 'expired'),
      expiring: personnelCerts.filter(c => c.complianceStatus === 'expiring'),
      valid: personnelCerts.filter(c => c.complianceStatus === 'valid'),
      missing: personnelCerts.filter(c => c.complianceStatus === 'missing')
    };
  }, [personnelCerts]);

  // Group site docs by status
  const groupedSiteDocs = useMemo(() => {
    return {
      overdue: siteDocs.filter(d => d.complianceStatus === 'overdue'),
      dueSoon: siteDocs.filter(d => d.complianceStatus === 'due_soon'),
      compliant: siteDocs.filter(d => d.complianceStatus === 'compliant'),
      notStarted: siteDocs.filter(d => d.complianceStatus === 'not_started')
    };
  }, [siteDocs]);

  // Calculate overall stats
  const overallStats = useMemo(() => {
    const personnel = stats.personnel || { total: 0, valid: 0, expiring: 0, expired: 0, missing: 0 };
    const site = stats.site || { total: 0, compliant: 0, dueSoon: 0, overdue: 0, notStarted: 0 };
    const policies = stats.policies || { total: 0, pending: 0, overdue: 0, acknowledged: 0 };

    const totalDocs = personnel.total + site.total;
    const compliantDocs = personnel.valid + site.compliant;
    const overallPercentage = totalDocs > 0
      ? Math.round((compliantDocs / totalDocs) * 100)
      : 0;

    return {
      personnel,
      site,
      policies,
      overall: {
        total: totalDocs,
        compliant: compliantDocs,
        needsAttention: personnel.expired + personnel.expiring + site.overdue + site.dueSoon,
        compliancePercentage: overallPercentage
      }
    };
  }, [stats]);

  // Initial load
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Re-fetch when filter changes in stack view
  useEffect(() => {
    if (isStackView) {
      fetchData();
    }
  }, [selectedSpaceFilter, isStackView, fetchData]);

  return {
    // Raw data
    personnelCerts,
    siteDocs,

    // Grouped data
    groupedPersonnelCerts,
    groupedSiteDocs,

    // Statistics
    stats: overallStats,

    // Loading state
    loading,
    error,

    // Actions
    refetch: fetchData
  };
};
